import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { LaborStationOperatorPresence } from "@/components/labor/LaborStationOperatorPresence";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { laborRemotePersistenceDisabled } from "@/lib/labor/laborEnv";
import { useLaborDevStatusPanelRegistration } from "@/lib/labor/laborDevStatusPanelContext";
import type { LaborStatusPanelProps } from "@/lib/labor/LaborStatusPanel";
import { useLaborSession } from "@/lib/labor/useLaborSession";
import { formatManufacturingLaborDuration } from "@/pages/clampqc/clampManufacturingLaborFormat";

import {
  encapsulationActivityLabel,
  encapsulationLaborPageKey,
  isEncapsulationActivity,
  type EncapsulationActivity,
} from "./encapsulationLaborPage";

const ENCAPSULATION_PAGE_HELP =
  "Clock in for potting or conformal coat. This station does not clock you out for inactivity. While you are clocked in, other hub pages stay blocked until you clock out. When you clock out, the session time is divided equally across the units that were on the potting or conformal coat station during the session (using Pre-Encapsulation routing and Final QC as the on-station and off-station proxies).";

export function EncapsulationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activityParam = searchParams.get("activity") ?? "";
  const activity = isEncapsulationActivity(activityParam) ? activityParam : null;

  const pickActivity = (next: EncapsulationActivity): void => {
    setSearchParams({ activity: next });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={ENCAPSULATION_PAGE_HELP}>Encapsulation</PageTitleWithHelp>
        <LaborStationOperatorPresence stationPage="encapsulation" />
      </div>
      {activity == null ? (
        <EncapsulationChooser onPick={pickActivity} />
      ) : (
        <EncapsulationStation
          key={activity}
          activity={activity}
          onLeave={() => setSearchParams({})}
          onSwitch={pickActivity}
        />
      )}
    </div>
  );
}

function EncapsulationChooser(props: { onPick: (activity: EncapsulationActivity) => void }) {
  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <p className="text-sm text-slate-700">Choose the work you are clocking in for.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Button className="h-16 text-base" onClick={() => props.onPick("potting")}>
            Potting
          </Button>
          <Button className="h-16 text-base" variant="secondary" onClick={() => props.onPick("conformal-coat")}>
            Conformal coat
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function EncapsulationStation(props: {
  activity: EncapsulationActivity;
  onLeave: () => void;
  onSwitch: (activity: EncapsulationActivity) => void;
}) {
  const label = encapsulationActivityLabel(props.activity);
  const otherActivity: EncapsulationActivity = props.activity === "potting" ? "conformal-coat" : "potting";
  const labor = useLaborSession({
    page: encapsulationLaborPageKey(props.activity),
    domain: "manufacturing",
    moduleGate: "manufacturing",
    devPersistenceDisabled: () => laborRemotePersistenceDisabled(),
    workLabel: `Encapsulation (${label})`,
    autoStart: true,
    disableIdleTimeout: true,
    requirePersistedClockIn: true,
    onSessionEndedRemotely: props.onLeave,
    onPersistedSessionLost: props.onLeave,
    localOnlyMessage: (reason) =>
      reason === "read-only"
        ? "Started working. Your role is read-only for Manufacturing, so this is a local timer only."
        : "Started working. Labor writes are blocked, so this is a local timer only.",
  });

  const snapshot = useMemo((): LaborStatusPanelProps => {
    return {
      isWorking: labor.isWorking,
      elapsedSeconds: labor.elapsedSeconds,
      workLabel: `Encapsulation (${label})`,
      banner: labor.banner,
      lastSummary: labor.lastSummary,
      heartbeatIntervalMs: labor.heartbeatIntervalMs,
      idleTimeoutMs: labor.idleTimeoutMs,
    };
  }, [
    labor.isWorking,
    labor.elapsedSeconds,
    labor.banner,
    labor.lastSummary,
    labor.heartbeatIntervalMs,
    labor.idleTimeoutMs,
    label,
  ]);

  useLaborDevStatusPanelRegistration(import.meta.env.DEV, snapshot);

  const clockOut = async (): Promise<void> => {
    await labor.stopWorking();
    props.onLeave();
  };

  const switchActivity = async (): Promise<void> => {
    if (labor.isWorking) {
      await labor.stopWorking();
    }
    props.onSwitch(otherActivity);
  };

  return (
    <Card>
      <CardBody className="flex flex-col gap-4">
        <div>
          <p className="text-sm font-medium text-slate-500">
            {labor.isWorking ? "Clocked in for" : labor.loading ? "Clocking in for" : "Encapsulation"}
          </p>
          <p className="text-2xl font-semibold text-slate-900">{label}</p>
        </div>
        <p className="font-mono text-4xl tabular-nums text-slate-900">
          {formatManufacturingLaborDuration(labor.loading ? 0 : labor.elapsedSeconds)}
        </p>
        {labor.banner ? (
          <p
            className={
              labor.banner.tone === "warning"
                ? "rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
                : "rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
            }
          >
            {labor.banner.message}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <Button variant="danger" className="h-12 px-6" disabled={labor.loading || !labor.isWorking} onClick={() => void clockOut()}>
            Clock out
          </Button>
          <Button
            variant="secondary"
            className="h-12 px-6"
            disabled={labor.loading}
            onClick={() => void switchActivity()}
          >
            Switch to {encapsulationActivityLabel(otherActivity)}
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
